import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

function calculateMonthsBetween(startDateString, endDateString) {
  if (!startDateString || !endDateString) return null;
  const start = new Date(startDateString);
  const end = new Date(endDateString);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diffMs = end.getTime() - start.getTime();
  const days = diffMs / (1000 * 60 * 60 * 24);
  const months = Math.abs(days / 30);
  return Math.round(months * 10) / 10;
}

function calculateMonthsFromToNow(startDateString) {
  if (!startDateString) return null;
  return calculateMonthsBetween(startDateString, new Date().toISOString().slice(0, 10));
}

export default function DelayedUniversityRegistrationsNotifications() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [uniRegFilter, setUniRegFilter] = useState('all'); // all | delayed | not-yet

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get('/admin/registrations/delayed-university-registrations');
        if (!cancelled) {
          const payload = response.data || {};
          setStudents(Array.isArray(payload.students) ? payload.students : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load delayed university registrations', err);
          setError('Failed to load delayed university registrations. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  const courseOptions = useMemo(() => {
    const map = new Map();
    students.forEach((s) => {
      if (s.courseId && !map.has(s.courseId)) {
        const schedule = s.courseScheduleType ? s.courseScheduleType.toLowerCase() : null;
        const scheduleLabel =
          schedule === 'weekend' ? 'Weekend' : schedule === 'weekday' ? 'Weekday' : null;
        map.set(s.courseId, {
          courseId: s.courseId,
          label:
            (s.courseShortName || s.courseName || `Course #${s.courseId}`) +
            (scheduleLabel ? ` - ${scheduleLabel}` : ''),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [students]);

  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    return students.filter((student) => {
      if (courseFilter !== 'all' && String(student.courseId) !== String(courseFilter)) {
        return false;
      }

      // University registration status filter
      const hasUniversityDate = Boolean(student.universityRegisterDate);
      if (uniRegFilter === 'delayed' && !hasUniversityDate) {
        return false;
      }
      if (uniRegFilter === 'not-yet' && hasUniversityDate) {
        return false;
      }

      if (!term) {
        return true;
      }

      const haystack = [
        student.studentName,
        student.studentId,
        student.nicNumber,
        student.registrationNumber,
        student.courseName,
        student.courseShortName,
        student.batchName,
        student.batchInternalName,
        student.whatsappGroupName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [students, search, courseFilter, uniRegFilter]);

  const totalCount = students.length;
  const visibleCount = filteredStudents.length;

  const handleDownloadExcel = async () => {
    if (!filteredStudents.length) {
      return;
    }

    try {
      const XLSX = await import('xlsx');

      const headers = [
        'Student ID',
        'Student Name',
        'NIC',
        'DOB',
        'Registration Number',
        'Registration Status',
        'Delivery Status',
        'University ID',
        'University Register Date',
        'Course Name',
        'Course Short Name',
        'Batch Name',
        'Batch Internal Name',
        'Batch Starting Date',
        'Batch Ending Date',
        'WhatsApp Group',
        'Delay Months (Batch End → Uni Reg)',
        'Months Since Uni Registration',
      ];

      const rows = filteredStudents.map((student) => {
        const delayMonths = calculateMonthsBetween(student.batchEndingDate, student.universityRegisterDate);
        const monthsSince = calculateMonthsFromToNow(student.universityRegisterDate);

        return [
          student.studentId || '',
          student.studentName || '',
          student.nicNumber || '',
          student.dob || '',
          student.registrationNumber || '',
          student.registrationStatus || '',
          student.deliveryStatus || '',
          student.universityID || '',
          student.universityRegisterDate || '',
          student.courseName || '',
          student.courseShortName || '',
          student.batchName || student.batchInternalName || '',
          student.batchInternalName || '',
          student.batchStartingDate || '',
          student.batchEndingDate || '',
          student.whatsappGroupName || '',
          delayMonths == null ? '' : delayMonths,
          monthsSince == null ? '' : monthsSince,
        ];
      });

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Delayed Uni Registrations');

      const today = new Date().toISOString().split('T')[0];
      const fileName = `Delayed_University_Registrations_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('Failed to download delayed university registrations Excel', err);
    }
  };

  return (
    <DashboardLayout title="Students with Delayed University Registration">
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-gray-900">Students with Delayed University Registration</h1>
          <p className="text-sm text-gray-600">
            This view lists students for whom university registration was completed after the batch end date, but within a
            12-month window.
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">{error}</div>
        )}

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 border border-amber-100">
              Total students with delayed university registration: <span className="ml-1 font-semibold">{totalCount}</span>
            </span>
            {totalCount > 0 && (
              <span className="text-xs text-gray-500">Showing {visibleCount} of {totalCount}</span>
            )}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end sm:gap-4 w-full sm:w-auto">
            <div className="w-full sm:w-52">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="delayed-uni-course-filter">
                Filter by course
              </label>
              <select
                id="delayed-uni-course-filter"
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              >
                <option value="all">All courses</option>
                {courseOptions.map((course) => (
                  <option key={course.courseId} value={course.courseId}>
                    {course.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-52">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="delayed-uni-status-filter">
                Filter by university registration
              </label>
              <select
                id="delayed-uni-status-filter"
                value={uniRegFilter}
                onChange={(e) => setUniRegFilter(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              >
                <option value="all">All</option>
                <option value="delayed">Delayed registered</option>
                <option value="not-yet">Not yet registered</option>
              </select>
            </div>
            <div className="w-full sm:w-64">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="delayed-uni-search">
                Search by student, registration, batch or course
              </label>
              <input
                id="delayed-uni-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to search..."
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
            </div>
            {visibleCount > 0 && (
              <button
                type="button"
                onClick={handleDownloadExcel}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download as Excel
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-gray-600">Loading delayed university registrations...</div>
        ) : visibleCount === 0 ? (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            No students currently match the delayed university registration criteria.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Student</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Registration</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">University number</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">University registered date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 w-80">Batch & Course</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Delay (months)</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Current Date (months)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredStudents.map((student) => (
                  <tr key={student.registrationId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 align-top">
                      <div className="font-medium text-gray-900">{student.studentName || 'N/A'}</div>
                      <div className="text-xs text-gray-500">ID: {student.studentId || 'N/A'}</div>
                      {student.nicNumber && (
                        <div className="text-xs text-gray-500">NIC: {student.nicNumber}</div>
                      )}
                      {student.dob && (
                        <div className="text-xs text-gray-500">DOB: {student.dob}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      <div className="font-medium text-gray-900">{student.registrationNumber}</div>
                      <div className="mt-1 inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 border border-slate-200">
                        {student.registrationStatus}
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500">
                        Delivery status: {student.deliveryStatus || 'Not set'}
                      </div>
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      {student.universityID ? (
                        <div>{student.universityID}</div>
                      ) : (
                        <div className="text-gray-500">Not set</div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      {student.universityRegisterDate ? (
                        <div>{student.universityRegisterDate}</div>
                      ) : (
                        <div className="text-gray-500">Not registered</div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      <div className="font-medium text-gray-900">
                        {student.courseShortName || student.courseName || 'N/A'}
                      </div>
                      <div className="text-[11px] text-gray-500">{student.courseName}</div>
                      <div className="mt-1 text-[11px] text-gray-700">
                        Batch: {student.batchName || student.batchInternalName || 'N/A'}
                      </div>
                      {student.batchInternalName && (
                        <div className="text-[11px] text-gray-500">Internal: {student.batchInternalName}</div>
                      )}
                      {student.batchStartingDate && (
                        <div className="text-[11px] text-gray-500">Batch start: {student.batchStartingDate}</div>
                      )}
                      {student.batchEndingDate && (
                        <div className="text-[11px] text-gray-500">Batch end: {student.batchEndingDate}</div>
                      )}
                      {student.whatsappGroupName && (
                        <div className="text-[11px] text-gray-500">WhatsApp: {student.whatsappGroupName}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      {(() => {
                        const months = calculateMonthsBetween(student.batchEndingDate, student.universityRegisterDate);
                        if (months === null) {
                          return 'Not yet registered';
                        }
                        const label = `${months.toString()} month${months === 1 ? '' : 's'}`;
                        return `${label} (Registered but delayed)`;
                      })()}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      {(() => {
                        const months = calculateMonthsFromToNow(student.universityRegisterDate);
                        if (months === null) {
                          return 'Not yet registered';
                        }
                        const label = `${months.toString()} month${months === 1 ? '' : 's'}`;
                        const isEnough = months >= 12;
                        const badgeClass = isEnough
                          ? 'inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-800 border border-green-200'
                          : 'inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-800 border border-red-200';
                        return <span className={badgeClass}>{label}</span>;
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
